import { Configs } from "../logic";
import { OAuthSalaxyAuthorizeMode, UserSession } from "../model";
import { Ajax } from "./infra";

/**
 * Provides read-only access to the current user session
 */
export class Session {

  /**
   * For NG1-dependency injection
   * @ignore
   */
  public static $inject = ["AjaxNg1"];

  constructor(private ajax: Ajax) {}

  /**
   * Checks whether the current browser session is authorized.
   *
   * @returns A Promise with result data (boolean).
   */
  public isAuthorized(): Promise<boolean> {
    return this.ajax.getJSON("/session/isAuthorized");
  }

  /**
   * Gets the current session information or null if there is no active session.
   * UserSession data is the Authorization object that provides information about the current session. Null, if there is no session.
   *
   * @returns A Promise with result data (UserSession).
   */
  public getSession(): Promise<UserSession> {
    return this.ajax.getJSON("/session/current");
  }

  /**
   * Returns server address
   */
  public getServerAddress(): string {
    return this.ajax.getServerAddress();
  }

  /**
   * Gets the URL for login / register UI to Salaxy API.
   *
   * @param redirectUrl The URL where the user is taken after a successfull login.
   * If not specified, uses window.location.protocol + "//" + window.location.host + window.location.pathname.
   * In non-browser environments, this must be specified.
   * @param role Optional role (household, worker or company) for the situations where it is known - mainly for new users.
   * @param partnerSite The partner site is passed as client_id to the sign-in process.
   * @param mode Salaxy login mode: "sign_in" (login screen) or "sign_up" (register as new user).
   * @param urlPostfix Additional string that is added to tha URL.
   * Used in adding other parameters to the URL, e.g. "&salaxy_language=en"
   */
   public getSignInAddress(
    redirectUrl: string = null,
    role: string = null,
    partnerSite: string = null,
    mode: OAuthSalaxyAuthorizeMode = null,
    urlPostfix: string = null,
  ): string {
    const config = Configs.current;
    const partner = partnerSite || config?.partnerSite || "unknown";
    if (!redirectUrl) {
      if (!window) {
        throw new Error("Redirect URL is required if window is not available.");
      }
      redirectUrl = window.location.protocol + "//" + window.location.host + window.location.pathname;
    }
    let url = this.getServerAddress() +
      "/oauth2/authorize?response_type=token&redirect_uri=" + encodeURIComponent(redirectUrl || "") +
      "&client_id=" + encodeURIComponent(partner || "") +
      "&salaxy_role=" + encodeURIComponent(role || "") +
      "&salaxy_authorize_mode=" + encodeURIComponent("" + mode);
    if (urlPostfix) {
      url = url + urlPostfix;
    }
    return url;
  }
}
